import { PrintRow } from '.';
import CloudMarkupGenerator, { StarPrintAlignType } from './CloudMarkupGenerator';

/* The Cloud Printer uses different terminology for alignment */
const MAP_ALIGNMENT: { [key: string]: StarPrintAlignType } = {
  LEFT: 'left',
  CENTER: 'centre',
  RIGHT: 'right',
};

const getFontSize = (fontSize: number): number => {
  if (fontSize > 64) {
    return 4;
  } else if (fontSize > 48) {
    return 3;
  } else if (fontSize > 35) {
    return 2;
  } else if (fontSize > 14) {
    return 1;
  } else {
    return 0;
  }
};

/* To apply styling we need to transform the PrintRows to the format the Cloud Printer requests */
export const generateCommands = (printPayload: PrintRow[]): string => {
  const generator = new CloudMarkupGenerator();

  printPayload.map((row) => {
    let fontHeight = row.fontSize ? getFontSize(row.fontSize) : 0;
    let fontWidth = row.fontSize ? getFontSize(row.fontSize) : 0;

    if (row.doubleHeigth) {
      fontHeight = fontHeight + 1;
    }

    if (row.doubleWidth) {
      fontWidth = fontWidth + 1;
    }

    if (fontHeight !== 0 || fontWidth !== 0) {
      generator.magnify(fontHeight, fontWidth);
    }

    if (row.bold) {
      generator.bold();
    }

    if (row.alignment) {
      MAP_ALIGNMENT[row.alignment];
      generator.align(MAP_ALIGNMENT[row.alignment]);
    }

    if (row.qr) {
      generator.qrCode(row.content);
    } else if (row.content) {
      generator.text(row.content);
    }
  });

  /* Always cut the print in the end */
  generator.cut();

  const binaryOutput = generator.exportAsBinary();

  return binaryOutput;
};

/**
 * Combines a logo image (with data URL prefix) and a text image (raw base64, no prefix)
 * into a single base64 image by drawing the logo on top of the text content.
 * Used for Zettle terminal
 */
export const combineLogoAndText = (
  logoDataUrl: string,
  textBase64: string,
  logger: { info: (msg: string, data?: any) => void },
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const logoImg = new Image();
    const textImg = new Image();

    let logoLoaded = false;
    let textLoaded = false;

    const tryMerge = () => {
      if (!logoLoaded || !textLoaded) return;

      try {
        const width = Math.max(logoImg.width, textImg.width);
        const totalHeight = logoImg.height + textImg.height;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = totalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // White background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, totalHeight);

        // Draw logo centered at top
        const logoX = Math.round((width - logoImg.width) / 2);
        ctx.drawImage(logoImg, logoX, 0);

        // Draw text below logo
        ctx.drawImage(textImg, 0, logoImg.height);

        const result = canvas.toDataURL('image/png').replace('data:image/png;base64,', '');
        logger.info('Combined logo and text image', {
          logoWidth: logoImg.width,
          logoHeight: logoImg.height,
          textWidth: textImg.width,
          textHeight: textImg.height,
          totalHeight,
        });
        resolve(result);
      } catch (e) {
        reject(e);
      }
    };

    logoImg.onload = () => {
      logoLoaded = true;
      tryMerge();
    };
    textImg.onload = () => {
      textLoaded = true;
      tryMerge();
    };
    logoImg.onerror = () => reject(new Error('Failed to load logo image'));
    textImg.onerror = () => reject(new Error('Failed to load text image'));

    logoImg.src = logoDataUrl;
    textImg.src = `data:image/png;base64,${textBase64}`;
  });
};
